import type { ChangeEventHandler, ComponentType } from 'react'
import { useCallback, useMemo, useRef, useState } from 'react'

import { NewMessageFanDto, OfChatNotificationsService } from '@api'
import { useUnit } from 'effector-react'
import debounce from 'lodash/debounce'
import times from 'lodash/times'
import { useInfiniteQuery } from 'react-query'
import type { ListChildComponentProps, ListOnScrollProps } from 'react-window'
import { VariableSizeList } from 'react-window'

import { IconLoading } from '@/assets/icons/icon-loading.tsx'
import SearchIcon from '@/assets/icons/icon-search.svg'
import { Dialog } from '@/components/ui/dialog.tsx'
import { EmptyTableContent } from '@/components/ui/empty/empty-table-content.tsx'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.tsx'
import { Radio, RadioGroup } from '@/components/ui/radio.tsx'
import { TextField } from '@/components/ui/text-field.tsx'
import type { DialogProps } from '@/dialogs/types'
import { $creatorId } from '@/models/creators.ts'
import {
  usersListOrderOptions,
  usersListSortOptions,
} from '@/modules/chatting/consts/usersList.ts'
import { useOpenChat } from '@/modules/chatting/hooks/use-open-chat.ts'
import SortIcon from '@/modules/chatting/icons/sort.svg'
import { NewMessageUsersListItem } from '@/modules/chatting/ui/components/NewMessageUsersListItem/NewMessageUsersListItem.tsx'
import { NewMessageUsersListItemSkeleton } from '@/modules/chatting/ui/components/NewMessageUsersListItem/NewMessageUsersListItemSkeleton.tsx'
import { IconButton } from '@/modules/chatting/ui/components/icon-button.tsx'
import { useSizeObserver } from '@/modules/chatting/ui/sidebar/use-size-observer.ts'
import { isTruthyAndNotEmpty } from '@/utils/isTruthy.ts'

const getItemSize = () => 82
const FANS_PAGINATION_SIZE = 10

interface Props extends DialogProps {}

export const FansListToStartChatWithDialog = ({ isOpen, onClose }: Props) => {
  const creatorId = useUnit($creatorId)
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState(usersListSortOptions[0].value)
  const [order, setOrder] = useState(usersListSortOptions[0].value)
  const [parentRef, width, height] = useSizeObserver()
  const lastVisibleIndexRef = useRef<number | null>(null)

  const { handleOpenChat } = useOpenChat()

  const handleChangeSearch: ChangeEventHandler<HTMLInputElement> = debounce(
    (event) => setQuery(event.target.value),
    500,
  )

  const { isFetchingNextPage, isLoading, data, hasNextPage, fetchNextPage } =
    useInfiniteQuery(
      ['newChatters', query, creatorId, order, sort, order],
      ({ pageParam = 0 }) =>
        OfChatNotificationsService.getUsers({
          creatorId: creatorId!,
          offset: FANS_PAGINATION_SIZE * pageParam,
          requestBody: {
            query,
            sort,
            order,
          },
        }),
      {
        getNextPageParam: (page, pages) =>
          page.hasMore ? pages.length : undefined,
        refetchOnMount: true,
        enabled: !!creatorId && isOpen,
      },
    )

  const chatters = useMemo(
    () =>
      data?.pages.flatMap((page) => page.list).filter(isTruthyAndNotEmpty) ??
      [],
    [data?.pages],
  )

  const onSelectChatter = useCallback(
    async ({ id }: NewMessageFanDto) => {
      await handleOpenChat(id)
      onClose()
    },
    [onClose, handleOpenChat],
  )

  const handleScroll = useCallback(
    ({ scrollDirection }: ListOnScrollProps) => {
      const lastItemIndex = lastVisibleIndexRef.current

      if (
        lastItemIndex === null ||
        lastItemIndex !== chatters.length - 1 ||
        !(hasNextPage && !isFetchingNextPage)
      ) {
        return
      }

      if (scrollDirection === 'forward') {
        fetchNextPage()
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage, chatters.length],
  )

  const renderChatItem: ComponentType<
    ListChildComponentProps<NewMessageFanDto[]>
  > = useCallback(
    ({ data, index, style }) => {
      lastVisibleIndexRef.current = index

      return (
        <NewMessageUsersListItem
          chatter={data[index]}
          style={style}
          onClick={onSelectChatter}
        />
      )
    },
    [onSelectChatter],
  )

  return (
    <Dialog
      show={isOpen}
      bigDialog
      onClose={onClose}
      childrenClassName="max-w-full overflow-hidden"
      panelClassName="lg:max-w-[500px] p-6 sm:p-8"
    >
      <div className="space-y-6">
        <Dialog.Header>
          <Dialog.Title>Select fan</Dialog.Title>
        </Dialog.Header>
        <div className="flex items-center gap-3">
          <TextField
            placeholder="Search"
            className="resize-none"
            prefix={
              <SearchIcon className="text-title dark:text-secondary-300" />
            }
            onChange={handleChangeSearch}
          />
          <Popover>
            <PopoverTrigger
              className="outline-none"
              asChild
            >
              <IconButton icon={SortIcon} />
            </PopoverTrigger>
            <PopoverContent className="z-100 w-[200px] p-2">
              <RadioGroup
                value={order}
                onChange={setOrder}
              >
                {usersListOrderOptions.map((option) => (
                  <Radio
                    className="p-1.5"
                    key={option.value}
                    labelClassName="text-sm font-medium leading-[22px]"
                    {...option}
                  />
                ))}
              </RadioGroup>
              <div className="w-full border-b" />
              <RadioGroup
                value={sort}
                onChange={setSort}
              >
                {usersListSortOptions.map((option) => (
                  <Radio
                    className="p-1.5"
                    key={option.value}
                    labelClassName="text-sm font-medium leading-[22px]"
                    {...option}
                  />
                ))}
              </RadioGroup>
            </PopoverContent>
          </Popover>
        </div>
        <div
          className="no-scrollbar bg-card relative h-[532px] overflow-hidden rounded-lg border border-border-secondary py-4"
          ref={parentRef}
        >
          {isLoading ? (
            times(3).map((num) => <NewMessageUsersListItemSkeleton key={num} />)
          ) : !chatters || chatters.length === 0 ? (
            <EmptyTableContent>
              No results found. <br />
              Please change filter criteria.
            </EmptyTableContent>
          ) : (
            <VariableSizeList
              onScroll={handleScroll}
              className="scrollbar-hide"
              itemData={chatters}
              itemSize={getItemSize}
              height={height}
              itemCount={chatters?.length ?? 0}
              width={width}
            >
              {renderChatItem}
            </VariableSizeList>
          )}
          {isFetchingNextPage && (
            <div className="bg-card absolute bottom-0 left-0 right-0">
              <IconLoading className="mx-auto my-3 animate-spin" />
            </div>
          )}
        </div>
        <Dialog.ClosableFooter
          onClose={onClose}
          className="grid-cols-1"
        />
      </div>
    </Dialog>
  )
}
